import { AnchorHTMLAttributes, FC, PropsWithChildren, ReactNode, useId } from 'react';

import { messageBarClass, messageBarOverlayWrapperClass, messageBarStandaloneClass } from './MessageBar.css';
import { ConditionalWrapper } from '../../internal/components/ConditionalWrapper';
import { getClassNames } from '../../util/css';
import { Box } from '../Box/Box';
import { Heading } from '../Heading/Heading';
import { LinkOverlay } from '../LinkOverlay/LinkOverlay';
import { PageGrid } from '../PageGrid/PageGrid';
import { Text } from '../Text/Text';

type Props = {
  headingLevel?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  href?: AnchorHTMLAttributes<HTMLAnchorElement>['href'];
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  text: ReactNode;
  title?: string;
};

type WrapperProps = {
  contentId: string;
} & Pick<Props, 'href' | 'target'>;

const LinkOverlayWrapper: FC<PropsWithChildren<WrapperProps>> = ({ children, contentId, href, target }) => (
  <LinkOverlay className={messageBarOverlayWrapperClass}>
    <LinkOverlay.Link aria-labelledby={contentId} href={href} {...(target ? { target } : null)}>
      {}
    </LinkOverlay.Link>
    {children}
  </LinkOverlay>
);
export const MessageBar: FC<PropsWithChildren<Props>> = ({ href, headingLevel = 'h4', target, title, text }) => {
  const contentId = useId();

  const wrapperProps: WrapperProps = {
    contentId,
    href,
    target: target,
  };

  return (
    <ConditionalWrapper shouldWrap={!!href} wrapper={LinkOverlayWrapper} {...wrapperProps}>
      <div className={getClassNames(messageBarClass, !href && messageBarStandaloneClass)} id={contentId}>
        <PageGrid>
          <PageGrid.Item gridColumn="1/-1">
            {title && <Heading as={headingLevel}>{title}</Heading>}
            <Box paddingTop="1">{typeof text === 'string' ? <Text>{text}</Text> : <div>{text}</div>}</Box>
          </PageGrid.Item>
        </PageGrid>
      </div>
    </ConditionalWrapper>
  );
};

MessageBar.displayName = 'MessageBar';
